import {SC_Address} from "./SC_Address";
import {SC_LocationService} from "./SC_LocationService";
import {SC_Originator} from "./enums/SC_Originator";
import {applyPAT} from "../LinkUtils";
import {SC_LocationFilter} from "./filters/SC_LocationFilter";
import {SC_PaginationState} from "./filters/SC_PaginationState";
import {SC_Client} from "./SC_Client";
import {SC_LocType} from "./enums/SC_LocType";
import {SC_YesNoEnum} from "./enums/SC_YesNoEnum";
import {SC_GenericConfirmation} from "./SC_GenericConfirmation";

export class SC_Location {
    /**
     * Input property name constants for fromAPI
     */
    static inputKeys = {
        internalID: 'internalID',
        buildingID: 'buildingID',
        eoid: 'eoid',
        externalID: 'externalID',
        originator: 'originator',
        bldType: 'buildingType',
        clientID: 'clientID',
        sitePhone: 'sitePhone',
        siteEmail: 'siteEmail',
        qcEmail: 'QCEmail',
        qcCcEmail: 'QCCCEmail',
        name: 'name',
        address: 'address',
        salesSize: 'salesSize',
        backHouse: 'backHouse',
        backHouseCleanable: 'backHouseCleanable',
        totalCleanable: 'totalCleanable',
        services: 'services',
        required_ivr: 'reqi',
        required_pictures: 'reqp',
        required_qc: 'reqq',
        qc_weekly: 'reqqw',
        confirmations: 'confirmations',
        preferred_contact_name: 'preferredContactName',
        preferred_contact_email: 'preferredContactEmail',
        preferred_contact_title: 'preferredContactTitle',
        preferred_contact_phone: 'preferredContactPhone',
    } as const;

    internalID: number | null;
    buildingID: string | null;
    eoid: string | null;
    externalID: string | null;
    originator: SC_Originator | null;
    bldType: SC_LocType | null;
    clientID: number | null;
    sitePhone: string | null;
    siteEmail: string | null;
    qcEmail: string[] | null;
    qcCcEmail: string[] | null;
    name: string | null;
    address: SC_Address | null;
    salesSize: number | null;
    backHouse: number | null;
    backHouseCleanable: boolean | null;
    totalCleanable: number | null;
    services: SC_LocationService[] | null;
    client: SC_Client | null = null;
    required_ivr: SC_YesNoEnum | null = null;
    required_pictures: SC_YesNoEnum | null = null;
    required_qc: SC_YesNoEnum | null = null;
    qc_weekly: SC_YesNoEnum | null = null;
    confirmations: SC_GenericConfirmation[] | null = null;
    preferred_contact_name: string | null = null;
    preferred_contact_email: string | null = null;
    preferred_contact_title: string | null = null;
    preferred_contact_phone: string | null = null;

    constructor({
        internalID = null,
        buildingID = null,
        eoid = null,
        externalID = null,
        originator = new SC_Originator(),
        bldType = new SC_LocType(),
        clientID = null,
        sitePhone = null,
        siteEmail = null,
        qcEmail = [],
        qcCcEmail = [],
        name = null,
        address = null,
        salesSize = null,
        backHouse = null,
        backHouseCleanable = null,
        totalCleanable = null,
        services = null,
        required_ivr = null,
        required_pictures = null,
        required_qc = null,
        qc_weekly = null,
        confirmations = null,
        preferred_contact_name = null,
        preferred_contact_email = null,
        preferred_contact_title = null,
        preferred_contact_phone = null,
    } = {}) {
        this.internalID = internalID;
        this.buildingID = buildingID;
        this.eoid = eoid;
        this.originator = originator;
        this.bldType = bldType;
        this.externalID = externalID;
        this.clientID = clientID;
        this.sitePhone = sitePhone;
        this.siteEmail = siteEmail;
        this.qcEmail = qcEmail;
        this.qcCcEmail = qcCcEmail;
        this.name = name;
        this.address = address;
        this.salesSize = salesSize;
        this.backHouse = backHouse;
        this.backHouseCleanable = backHouseCleanable;
        this.totalCleanable = totalCleanable;
        this.services = services;
        this.required_ivr = required_ivr;
        this.required_pictures = required_pictures;
        this.required_qc = required_qc;
        this.qc_weekly = qc_weekly;
        this.confirmations = confirmations;
        this.preferred_contact_name = preferred_contact_name;
        this.preferred_contact_email = preferred_contact_email;
        this.preferred_contact_title = preferred_contact_title;
        this.preferred_contact_phone = preferred_contact_phone;
    }

    /**
     * @param {Object} jsonin
     * @param {SC_Client[]} clientDict
     * @return {SC_Location}
     */
    static fromAPI(jsonin: Record<string, any>, clientDict: SC_Client[] = []): SC_Location {
        const out = new SC_Location({
            internalID: jsonin[SC_Location.inputKeys.internalID] ?? null,
            buildingID: jsonin[SC_Location.inputKeys.buildingID] ?? null,
            eoid: jsonin[SC_Location.inputKeys.eoid] ?? null,
            externalID: jsonin[SC_Location.inputKeys.externalID] ?? null,
            originator: SC_Originator.fromAPI(jsonin[this.inputKeys.originator]),
            bldType: SC_LocType.fromAPI(jsonin[this.inputKeys.bldType]),
            clientID: jsonin[SC_Location.inputKeys.clientID] ?? null,
            sitePhone: jsonin[SC_Location.inputKeys.sitePhone] ?? null,
            siteEmail: jsonin[SC_Location.inputKeys.siteEmail] ?? null,
            qcEmail: jsonin[SC_Location.inputKeys.qcEmail] ?? null,
            qcCcEmail: jsonin[SC_Location.inputKeys.qcCcEmail] ?? null,
            name: jsonin[SC_Location.inputKeys.name] ?? null,
            address: jsonin[SC_Location.inputKeys.address] ? SC_Address.fromAPI(jsonin[SC_Location.inputKeys.address]) : null,
            salesSize: jsonin[SC_Location.inputKeys.salesSize] ?? null,
            backHouse: jsonin[SC_Location.inputKeys.backHouse] ?? null,
            backHouseCleanable: jsonin[SC_Location.inputKeys.backHouseCleanable] ?? null,
            totalCleanable: jsonin[SC_Location.inputKeys.totalCleanable] ?? null,
            services: jsonin[SC_Location.inputKeys.services]?.map(o => SC_LocationService.fromAPI(o)).sort((a, b) => a.serviceName.toLowerCase().localeCompare(b.serviceName)) ?? null,
            required_ivr: jsonin[SC_Location.inputKeys.required_ivr] ? SC_YesNoEnum.fromAPI(jsonin[SC_Location.inputKeys.required_ivr]) : null,
            required_pictures: jsonin[SC_Location.inputKeys.required_pictures] ? SC_YesNoEnum.fromAPI(jsonin[SC_Location.inputKeys.required_pictures]) : null,
            required_qc: jsonin[SC_Location.inputKeys.required_qc] ? SC_YesNoEnum.fromAPI(jsonin[SC_Location.inputKeys.required_qc]) : null,
            qc_weekly: jsonin[SC_Location.inputKeys.qc_weekly] ? SC_YesNoEnum.fromAPI(jsonin[SC_Location.inputKeys.qc_weekly]) : null,
            confirmations: jsonin[SC_Location.inputKeys.confirmations]?.map((c: Record<string, any>) => SC_GenericConfirmation.fromAPI(c)) ?? null,
            preferred_contact_name: jsonin[SC_Location.inputKeys.preferred_contact_name] ?? null,
            preferred_contact_email: jsonin[SC_Location.inputKeys.preferred_contact_email] ?? null,
            preferred_contact_title: jsonin[SC_Location.inputKeys.preferred_contact_title] ?? null,
            preferred_contact_phone: jsonin[SC_Location.inputKeys.preferred_contact_phone] ?? null,
        });

        if (clientDict)
            out.scavengeClient(clientDict);

        return out;
    }

    toAPI(): Record<string, unknown> {
        return {
            [SC_Location.inputKeys.internalID]: this.internalID,
            [SC_Location.inputKeys.buildingID]: this.buildingID,
            [SC_Location.inputKeys.eoid]: this.eoid,
            [SC_Location.inputKeys.externalID]: this.externalID,
            [SC_Location.inputKeys.originator]: this.originator.toAPI(),
            [SC_Location.inputKeys.bldType]: this.bldType.toAPI(),
            [SC_Location.inputKeys.clientID]: this.clientID,
            [SC_Location.inputKeys.sitePhone]: this.sitePhone,
            [SC_Location.inputKeys.siteEmail]: this.siteEmail,
            [SC_Location.inputKeys.qcEmail]: this.qcEmail,
            [SC_Location.inputKeys.qcCcEmail]: this.qcCcEmail,
            [SC_Location.inputKeys.name]: this.name,
            [SC_Location.inputKeys.address]: this.address?.toAPI() ?? null,
            [SC_Location.inputKeys.salesSize]: this.salesSize,
            [SC_Location.inputKeys.backHouse]: this.backHouse,
            [SC_Location.inputKeys.backHouseCleanable]: this.backHouseCleanable,
            [SC_Location.inputKeys.totalCleanable]: this.totalCleanable,
            [SC_Location.inputKeys.required_ivr]: this.required_ivr?.toAPI() ?? null,
            [SC_Location.inputKeys.required_pictures]: this.required_pictures?.toAPI() ?? null,
            [SC_Location.inputKeys.required_qc]: this.required_qc?.toAPI() ?? null,
            [SC_Location.inputKeys.qc_weekly]: this.qc_weekly?.toAPI() ?? null,
            [SC_Location.inputKeys.confirmations]: this.confirmations?.map(it => it.toAPI()) ?? null,
            [SC_Location.inputKeys.preferred_contact_name]: this.preferred_contact_name,
            [SC_Location.inputKeys.preferred_contact_email]: this.preferred_contact_email,
            [SC_Location.inputKeys.preferred_contact_title]: this.preferred_contact_title,
            [SC_Location.inputKeys.preferred_contact_phone]: this.preferred_contact_phone,
        };
    }

    isDegenerate(): void {

    }

    clone(): SC_Location {
        return new SC_Location({
            internalID: this.internalID,
            buildingID: this.buildingID,
            eoid: this.eoid,
            externalID: this.externalID,
            originator: this.originator.clone(),
            bldType: this.bldType.clone(),
            clientID: this.clientID,
            sitePhone: this.sitePhone,
            siteEmail: this.siteEmail,
            qcEmail: [...this.qcEmail],
            qcCcEmail: [...this.qcCcEmail],
            name: this.name,
            address: this.address?.clone() ?? null,
            salesSize: this.salesSize,
            backHouse: this.backHouse,
            backHouseCleanable: this.backHouseCleanable,
            totalCleanable: this.totalCleanable,
            services: this.services?.map(service => service.clone()) ?? null,
            required_ivr: this.required_ivr,
            required_pictures: this.required_pictures,
            required_qc: this.required_qc,
            qc_weekly: this.qc_weekly,
            confirmations: this.confirmations?.map(it => it.clone()) ?? null,
            preferred_contact_name: this.preferred_contact_name,
            preferred_contact_email: this.preferred_contact_email,
            preferred_contact_title: this.preferred_contact_title,
            preferred_contact_phone: this.preferred_contact_phone,
        });
    }

    /**
     * Merges another SC_Location object into this one, replacing null fields
     */
    merge(other: SC_Location): SC_Location {
        if (other.internalID !== null) this.internalID = other.internalID;
        if (other.buildingID !== null) this.buildingID = other.buildingID;
        if (other.eoid !== null) this.eoid = other.eoid;
        if (other.externalID !== null) this.externalID = other.externalID;
        if (other.originator !== null && !other.originator.is(SC_Originator.NULL)) this.originator = other.originator;
        if (other.bldType !== null && !other.bldType.is(SC_LocType.NULL)) this.bldType = other.bldType;
        if (other.clientID !== null) this.clientID = other.clientID;
        if (other.sitePhone !== null) this.sitePhone = other.sitePhone;
        if (other.siteEmail !== null) this.siteEmail = other.siteEmail;
        if (other.name !== null) this.name = other.name;
        if (other.salesSize !== null) this.salesSize = other.salesSize;
        if (other.backHouse !== null) this.backHouse = other.backHouse;
        if (other.backHouseCleanable !== null) this.backHouseCleanable = other.backHouseCleanable;
        if (other.totalCleanable !== null) this.totalCleanable = other.totalCleanable;
        if (other.required_ivr !== null) this.required_ivr = other.required_ivr;
        if (other.required_pictures !== null) this.required_pictures = other.required_pictures;
        if (other.required_qc !== null) this.required_qc = other.required_qc;
        if (other.qc_weekly !== null) this.qc_weekly = other.qc_weekly;
        if (other.confirmations !== null) this.confirmations = other.confirmations;
        if (other.preferred_contact_name !== null) this.preferred_contact_name = other.preferred_contact_name;
        if (other.preferred_contact_email !== null) this.preferred_contact_email = other.preferred_contact_email;
        if (other.preferred_contact_title !== null) this.preferred_contact_title = other.preferred_contact_title;
        if (other.preferred_contact_phone !== null) this.preferred_contact_phone = other.preferred_contact_phone;

        // Handle qcEmail array
        if (other.qcEmail !== null && other.qcEmail.length > 0) {
            this.qcEmail = [...other.qcEmail];
        }

        // Handle qcCcEmail array
        if (other.qcCcEmail !== null && other.qcCcEmail.length > 0) {
            this.qcCcEmail = [...other.qcCcEmail];
        }

        // Handle address
        if (other.address !== null && this.address === null) {
            this.address = other.address.clone();
        } else if (other.address !== null && this.address !== null) {
            this.address.merge(other.address);
        }

        // Handle services array
        if (other.services !== null) {
            this.services = other.services.map(it => it.clone());
        }

        return this;
    }

    /**
     * @return {Promise<{pages: number, locations: SC_Location[]}>}
     */
    static getLocations(filter: SC_LocationFilter, pagination: SC_PaginationState, doRequest: (formData: FormData, action: string, doLoading: boolean) => Promise<any>, clientsCache: SC_Client[] = [], doLoading = true): Promise<{pages: number, locations: SC_Location[]}> {
        return new Promise(async (resolve, reject) => {
            let formData = new FormData();

            filter.applyToFormData(formData);

            pagination.applyToFormData(formData);

            let back = await doRequest(formData, "list_locations", doLoading);

            /**
             * @type {{pages: number, locations: SC_Location[]}}
             */
            let out = { pages: back.reason.total, locations: back.reason.locations.map((loc) => SC_Location.fromAPI(loc, clientsCache)) };

            resolve(out);
        });
    }

    scavengeClient(clients: SC_Client[]): void {
        if (clients) {
            const client = clients.find(client => client.internalID === this.clientID);
            if (client)
                this.client = client;
        }
    }

    static basicEmpty(): SC_Location {
        return new SC_Location({
            address: SC_Address.basicEmpty(),
            internalID: -1
        });
    }

    get locationID(): string {
        const abbr = this.client?.abbreviation ?? null;

        return `${abbr ? abbr + '-' : ''}${this.buildingID ?? "UNKNOWN"}`;
    }

    get internal_href(): string {
        return applyPAT(`/neo-list-locations/?location_id=${this.internalID}`);
    }
}
